import { SessionCreateResponse } from 'openai/resources/beta/realtime';
import { ConversationItemWithReference } from 'openai/resources/beta/realtime/realtime';
import { z } from 'zod';
import { ApplicationState, QuestionState } from './application_state.types';
import { FileSearchTool } from '../survey';

export const FeedbackSchema = z.object({
  rating: z.enum(["up", "down"]).nullable().optional(),
  reason: z.string().nullable().optional(),
  additionalFeedback: z.string().nullable().optional()
});

export const AKMetaSchema = z.object({
  time: z.number().optional(),
  version: z.number().optional(),
  phase: z.enum(["in_process", "pending", "done"]).optional(),
  done: z.boolean().optional(),
  createResponse: z.boolean().optional(),
  fromURL: z.string().optional(),
  tags: z.array(z.string()).optional(),
  feedback: FeedbackSchema.optional(),
  messageTemplate: z.object({
    name: z.string(),
    version: z.number()
  }).optional(),
  responseMeta: z.object({
    // References the .ai_app_state table 
    appStateId: z.string().optional(),
    modelMeta: z.object({
      model: z.string(),
      total_tokens: z.number().optional()
    }).catchall(z.any()), // TODO eventually remove this catchall
    errors: z.array(z.any()).optional()
  }).optional(),
  generatorMeta: z.object({
    stepIndex: z.number()
  }).optional()
});

export type AKMeta = z.infer<typeof AKMetaSchema>;

export type ConversationItemWithAKMeta = ConversationItemWithReference & { meta?: AKMeta };

export type FileCitation = {
  type: 'file_citation',
  index: number,
  fileName?: string,
  /**
   * The file that was used 
   */
  fileId: string
};

export type FreeformCitation = {
  type: 'freeform',
  details: Record<string, string>
}

export type TextAnnotation = FileCitation | FreeformCitation;

const TextAnnotationSchema = z.object({
  type: z.literal('file_citation'),
  index: z.number(),
  fileName: z.string().optional(),
  fileId: z.string()
}).or(z.object({
  type: z.literal('freeform'),
  details: z.any()
})) satisfies z.ZodType<TextAnnotation>;

export type TextContentPart = {
  type: 'text',
  content: string,
  originalContent?: string,
  annotations?: TextAnnotation[]
}

/**
 * Specifies the types of content we currently support.
 */
export type ContentPart =
  TextContentPart
  | {
    type: 'image',
    url: string,
    parsedText?: string,
    /**
     * Represents an ID of a Document in the Document Library.
     * Useful for evaluating LLM outcomes later on.
     */
    docId?: string,
  }
  ;

export type ContentPartTypes = {
  [K in ContentPart['type']]: Extract<ContentPart, { type: K }>
};


// TODO(luke): make this a union once we have more than 1 type of content
export const ContentPartSchema = z.union([
  z.object({
    type: z.literal('text'),
    content: z.string(),
    originalContent: z.string().optional(),
    annotations: TextAnnotationSchema.array().optional()
  }),
  z.object({
    type: z.literal('image'),
    url: z.string(),
    parsedText: z.string().optional(),
    docId: z.string().optional()
  })
]) satisfies z.ZodType<ContentPart>;


export type BaseItem = {

  /**
   * The Message ID to update
   */
  id: string,

  /**
   * User ID
   */
  createdBy?: string

  /**
   * Any metadata related to the chat item.
   */
  meta?: AKMeta
}

export const BaseItemSchema = z.object({
  id: z.string(),
  createdBy: z.string().optional(),
  meta: AKMetaSchema.optional(),
}) satisfies z.ZodType<BaseItem>;

/**
 * Represents a text message
 */
export type MessageItem = {
  type: 'message',
  role: 'user' | 'assistant' | 'system',
  content: ContentPart[]
};

export const MessageItemSchema = z.object({
  type: z.literal('message'),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.array(ContentPartSchema)
}) satisfies z.ZodType<MessageItem>;

/**
 * Represents a tool call request
 */
export type ToolCallItem = {
  type: 'tool_call',
  /**
   * The ID used to tie this call to the resulting output chat item (ToolCallOutputItem)
   */
  callId: string,
  /**
   * The name of the tool to invoke
   */
  name: string,
  /**
   * This must be a valid JSON object, in string form.
   */
  arguments: string
};

export const ToolCallItemSchema = z.object({
  type: z.literal('tool_call'),
  callId: z.string(),
  name: z.string(),
  arguments: z.string()
}) satisfies z.ZodType<ToolCallItem>;

/**
 * Represents the result of a tool call
 */
export type ToolCallOutputItem = {
  type: 'tool_call_output',
  previousItemId?: string,
  /**
   * The ID used to tie this call to the preceeding chat item (ToolCallItem)
   */
  callId: string,
  output: string
};

export const ToolCallOutputItemSchema = z.object({
  type: z.literal('tool_call_output'),
  previousItemId: z.string().optional(),
  callId: z.string(),
  output: z.string(),
}) satisfies z.ZodType<ToolCallOutputItem>;

export type AppStateItem = {
  type: 'appState',
  appState: ApplicationState
}

export const QuestionStateSchema = z.object({
  sectionId: z.string(),
  visible: z.boolean(),
  content: z.string(),
  type: z.string(),
  targetField: z.string(),
  required: z.boolean(),
  options: z
    .array(
      z.object({
        name: z.string(),
        content: z.string(),
        terminatesApplication: z.boolean().optional(),
        fieldType: z.enum(["custom-option", "select-all", "other"]),
      })
    )
    .optional(),
  completed: z.boolean().optional(),
  validity: z
    .object({
      state: z.enum(["unentered", "valid", "invalid", "warning"]),
      message: z.string().optional(),
    })
    .optional(),
  isNext: z.boolean().optional(),
  value: z.string().optional(),
}) satisfies z.ZodType<QuestionState>;

export const SectionStateSchema = z.object({
  name: z.string(),
  complete: z.boolean().optional(),
  visitable: z.boolean().optional(),
  viewing: z.boolean().optional()
});

export const AppStateItemSchema = z.object({
  type: z.literal('appState'),
  appState: z.object({
    currentSurveyPath: z.string().optional(),
    sections: z.record(z.string(), z.any()),
    questions: z.record(z.string(), z.any()),
    currentSection: SectionStateSchema.optional(),
    currentQuestion: QuestionStateSchema.optional()
  })
}) satisfies z.ZodType<AppStateItem>;

export type CommsMessage = {
  id: string | number,
  kind: 'sms' | 'email' | 'whatsapp' | 'call',
  source: string,
  destination: string,
  message: string,
  created_at: string,
  author_name?: string,
  status?: string,
  direction: 'inbound' | 'outbound',
}

export type CommsCorrespondence = {
  applicantUid: string,
  messages: CommsMessage[],
}

export type CommsItem = {
  type: 'comms',
  comms: CommsCorrespondence,
}

export const CommsMessageSchema = z.object({
  id: z.union([z.string(), z.number()]),
  kind: z.enum(['sms', 'email', 'whatsapp', 'call']),
  source: z.string(),
  destination: z.string(),
  message: z.string(),
  created_at: z.string(),
  author_name: z.string().optional(),
  status: z.string().optional(),
  direction: z.enum(['inbound', 'outbound']),
}) satisfies z.ZodType<CommsMessage>;

export const CommsItemSchema = z.object({
  type: z.literal('comms'),
  comms: z.object({
    applicantUid: z.string(),
    messages: z.array(CommsMessageSchema),
  }),
}) satisfies z.ZodType<CommsItem>;

/**
 * A published support template, shaped for inclusion in a prompt. Carried by a
 * `SupportTemplatesItem` so callers can fetch + supply support templates as a
 * chat item; the prompt assembler positions them via a `Support Templates`
 * instruction.
 */
export type SupportTemplateForPrompt = {
  name: string,
  id: number,
  categories: string[],
  body: string,
  description: string
}

export type SupportTemplatesItem = {
  type: 'supportTemplates',
  supportTemplates: SupportTemplateForPrompt[],
}

export const SupportTemplateForPromptSchema = z.object({
  name: z.string(),
  id: z.number(),
  categories: z.array(z.string()),
  body: z.string(),
  description: z.string(),
}) satisfies z.ZodType<SupportTemplateForPrompt>;

export const SupportTemplatesItemSchema = z.object({
  type: z.literal('supportTemplates'),
  supportTemplates: z.array(SupportTemplateForPromptSchema),
}) satisfies z.ZodType<SupportTemplatesItem>;

/**
 * Represents an internal (us) or external (them) error.
 *
 * e.g.
 */
export type ErrorItem = Partial<BaseItem> & {
  type: 'error',
  error: {
    type?: string | null,
    code?: string | null,
    message?: string | null,
    eventId?: string | null
  }
};

export const ErrorItemSchema = z.object({
  type: z.literal('error'),
  error: z.object({
    type: z.string().nullish(),
    code: z.string().nullish(),
    message: z.string().nullish(),
    eventId: z.string().nullish()
  })
}) satisfies z.ZodType<ErrorItem>;

/**
 * Indicates that an item has new metadata associated with it.
 *
 * e.g. feedback (thumbs up/down), phase (in progress, done), version...
 */
export type MetaUpdate = {
  /**
   * The Message ID to update
   */
  id: string,
  meta: Partial<AKMeta>,
  type: 'update_meta'
};

/**
 * Indicates that the event may be ignored.
 * This is typically used for events that are not yet supported,
 * as it is a way to 'safely' acknowledge them without erroring.
 */
export type Ignore = {
  type: 'ignore',
  sourceObj?: unknown
}

export type ReasoningItem = {
  type: 'reasoning',
  summary: string[],
  text?: string[],
  encryptedText?: string
};

export const ReasoningItemSchema = z.object({
  type: z.literal('reasoning'),
  summary: z.string().array(),
  text: z.string().array().optional(),
  encryptedText: z.string().optional()
}) satisfies z.ZodType<ReasoningItem>;

/**
 * This type is responsible for managing the valid message types 
 * which may exist in the chat.
 */
export type ChatMessageItem =
  BaseItem & (
    MessageItem
    | ToolCallItem
    | ToolCallOutputItem
    | AppStateItem
    | CommsItem
    | SupportTemplatesItem
    | ReasoningItem
  )
  | ErrorItem;

export const ChatMessageItemSchema = z.intersection(
  BaseItemSchema,
  MessageItemSchema
    .or(AppStateItemSchema)
    .or(CommsItemSchema)
    .or(SupportTemplatesItemSchema)
    .or(ToolCallItemSchema)
    .or(ToolCallOutputItemSchema)
    .or(ReasoningItemSchema)
).or(ErrorItemSchema) satisfies z.ZodType<ChatMessageItem>

/**
 * Helper Type to grab "completed" types from ChatMessageItem.
 * This is needed because most types are unioned with BaseItem (rather than every single type having base item unioned individually)
 */
export type ChatMessageItemTypes = {
  [K in ChatMessageItem['type']]: Extract<ChatMessageItem, { type: K }>
}

export type ContentPartDelta = {
  type: 'content_delta',
  index: number,
  content: ContentPart,
  overwrite?: boolean
};

export type RemoveItem = {
  type: 'remove_item'
};

export type UpdateItem = {
  type: 'update_item',
  with: ChatMessageItem
};

/**
 * This type is responsible for managing the valid chat message update types.
 */
export type ChatMessageUpdate =
  (BaseItem & (
    MessageItem
    | AppStateItem
    | CommsItem
    | SupportTemplatesItem
    | (Partial<ToolCallItem> & { type: 'tool_call', id: string })
    | ToolCallOutputItem
    | ContentPartDelta
    | RemoveItem
    | UpdateItem
    | ReasoningItem
  )
    | MetaUpdate
    | ErrorItem
    | Ignore)
  ;

/**
 * Helper Type to grab "completed" types from ChatMessageUpdate.
 * This is needed because most types are unioned with BaseItem (rather than every single type having base item unioned individually)
 */
export type ChatMessageUpdateTypes = {
  [K in ChatMessageUpdate['type']]: Extract<ChatMessageUpdate, { type: K }>
}

export type Interaction<T extends ChatMessageItem = ChatMessageItem> = {
  appState?: ChatMessageItemTypes["appState"],
  /**
   * A comms correspondence (e.g. a support ↔ applicant SMS/email thread) that
   * preceded the coach message. Present for support-draft conversations, where
   * the model drafts a reply from a human correspondence rather than from a
   * direct user message.
   */
  comms?: ChatMessageItemTypes["comms"],
  userMessages: (T & ChatMessageItemTypes['message'])[],
  coachMessage: (T & ChatMessageItemTypes['message']),
};

export function getInteractionsFromConversationItems<T extends ChatMessageItem = ChatMessageItem>(items: T[]): Interaction<T>[] {
  const result: Interaction<T>[] = [];
  type MessageItemType = T & ChatMessageItemTypes['message'];
  items.forEach((item, i, arr) => {
    if (item.type === "message" && item.role === "assistant") {
      const interaction: Interaction<T> = {
        userMessages: [] as MessageItemType[],
        coachMessage: item as MessageItemType,
      }

      // walk backwards and gather appState, comms, and user messages
      while (true) {
        i--;
        const prevItem = arr[i] as ChatMessageItemTypes['message'] | ChatMessageItemTypes["appState"] | ChatMessageItemTypes["comms"];
        if (!prevItem || (prevItem as ChatMessageItemTypes["message"]).role === "assistant") {
          break;
        }
        else if ((prevItem as ChatMessageItemTypes["message"]).role === "user") {
          interaction.userMessages.unshift(prevItem as MessageItemType);
        }
        else if ((prevItem as ChatMessageItemTypes["appState"]).type === "appState") {
          interaction.appState = prevItem as ChatMessageItemTypes["appState"];
        }
        else if ((prevItem as ChatMessageItemTypes["comms"]).type === "comms") {
          interaction.comms = prevItem as ChatMessageItemTypes["comms"];
        }
      }
      // A coach message forms an interaction when it responds either to user
      // message(s) or to a comms correspondence (support-draft conversations).
      if ((interaction.userMessages.length || interaction.comms) && interaction.coachMessage) {
        result.push(interaction);
      }
    }
  });
  return result;
}

export type AIConversation = {
  id: string,
  origin_id?: string,
  created_at: Date,
  updated_at: Date,
  created_by?: string,
  metadata?: {
    webrtc_session?: Omit<SessionCreateResponse, 'client_secret'>
  },
  items?: ChatMessageItem[]
}

export type AIConversationWithItems = AIConversation & {
  items: ChatMessageItem[]
}

//TODO(luke): add support for function tools
export type ToolDef = FileSearchTool;

export type FunctionDef<T extends z.ZodObject<any>, Out> = {
  type: 'function',
  name: string,
  description: string,
  schema: T,
  fn: (args: z.infer<T>) => Promise<Out>
};
